/**
 * Maschinenverwaltung - Liste
 * 
 * Funktionen:
 * - Liste mit Suche (Name, Typ)
 * - Navigation zu Detailseite (Create/Edit)
 * 
 * UX-Leitlinie: Keine Modals für Create/Edit, nur Liste → Detailseite
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { WashingMachine, Plus, RefreshCw, Search, ChevronRight } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface MachineTypeEntity {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface Machine {
  id: string;
  name: string;
  type: string;
  machineTypeId: string;
  machineType?: MachineTypeEntity;
  capacityKg: number;
  minLoadKg: number;
  maxLoadKg: number;
  overloadTolerance: number;
  isActive: boolean;
}

const sortAlpha = <T extends { name: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
};

export default function MachinesPage() {
  const navigate = useNavigate();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [machineTypes, setMachineTypes] = useState<MachineTypeEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Load machines and machine types
  const loadMachines = async () => {
    setLoading(true);
    try {
      const [machinesRes, typesRes] = await Promise.all([
        fetch('/api/admin/machines', { credentials: 'include' }),
        fetch('/api/admin/machine-types', { credentials: 'include' }),
      ]);
      if (machinesRes.ok) {
        const data = await machinesRes.json();
        setMachines(data);
      }
      if (typesRes.ok) {
        const data = await typesRes.json();
        setMachineTypes(data.filter((t: MachineTypeEntity) => t.isActive));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Maschinen:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadMachines();
  }, []);

  // Filter (Name, Typ) + alphabetisch sortieren
  const filtered = useMemo(() => {
    const active = machines.filter(m => m.isActive !== false);
    const searched = !search ? active : active.filter(m => {
      const s = search.toLowerCase();
      return m.name.toLowerCase().includes(s) ||
        m.machineType?.name.toLowerCase().includes(s);
    });
    return sortAlpha(searched);
  }, [machines, search]);

  // Get machine type by ID
  const getMachineType = (typeId: string) => machineTypes.find(t => t.id === typeId);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <WashingMachine className="w-6 h-6" />
              Maschinen
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {filtered.length} von {machines.filter(m => m.isActive !== false).length} Maschinen
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadMachines}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={() => navigate('/admin/machines/new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neue Maschine
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Suchen (Name, Typ)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {search ? 'Keine Treffer' : 'Keine Maschinen vorhanden'}
              {!search && (
                <button 
                  onClick={() => navigate('/admin/machines/new')}
                  className="block mx-auto mt-4 text-blue-600 hover:underline"
                >
                  Erste Maschine anlegen
                </button>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Typ</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase hidden sm:table-cell">Kapazität</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase hidden md:table-cell">Min/Max</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(m => (
                  <tr 
                    key={m.id} 
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/admin/machines/${m.id}`)}
                  >
                    <td className="px-4 py-3 font-medium">{m.name}</td>
                    <td className="px-4 py-3">
                      {(() => {
                        const mt = getMachineType(m.machineTypeId);
                        return mt ? (
                          <span className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-800">
                            {mt.name}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-800">{m.type}</span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right hidden sm:table-cell">{m.capacityKg} kg</td>
                    <td className="px-4 py-3 text-right text-slate-600 hidden md:table-cell">
                      {m.minLoadKg}–{m.maxLoadKg} kg
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
